/**
 * Admin Page
 *
 * Purpose: Central administration page providing access to all admin tools and systems.
 *          Uses SkeletonPage template for consistent layout with TopBanner and LeftSidebar.
 *
 * Architecture: Uses SkeletonPage template with admin-specific content including links to
 *              UIDemo, Metadata Manager, and Master Data Manager.
 *
 * Dependencies: React, Material-UI, SkeletonPage, AuthContext, React Router
 * Author: Development Team
 * Created: 2025-08-07
 * Updated: 2025-08-07 - Updated to use SkeletonPage template
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PaletteOutlined,
  SettingsApplicationsOutlined,
  SecurityOutlined,
  StorageOutlined,
  AdminPanelSettingsOutlined,
  HourglassEmptyOutlined,
  ScienceOutlined,
  NewReleasesOutlined,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  AccountTree as AccountTreeIcon,
  SmartToyOutlined,
  PsychologyOutlined,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { ModuleKey } from '../../constants/permissionKeys';
import SkeletonPage from '../../components/layout/SkeletonPage';
import { ContentHeader, ComingSoonCard } from '../../components/common/StyledComponents';

// Styled components
/**
 * A tile's status. 'available' is the default and shows no chip (ORCH-416): every tile on the
 * page is available, so only a status worth calling out gets one. A module that isn't ready is
 * left off the page, unless it is deliberately shown as 'coming-soon'.
 */
type ToolStatus = 'available' | 'beta' | 'new' | 'coming-soon';

const TOOL_STATUS_CHIPS: Record<
  Exclude<ToolStatus, 'available'>,
  { label: string; color: 'default' | 'info' | 'secondary'; icon: React.ReactElement }
> = {
  beta: { label: 'Beta', color: 'info', icon: <ScienceOutlined /> },
  new: { label: 'New', color: 'secondary', icon: <NewReleasesOutlined /> },
  'coming-soon': { label: 'Coming Soon', color: 'default', icon: <HourglassEmptyOutlined /> },
};

const ToolStatusChip: React.FC<{ status: ToolStatus }> = ({ status }) => {
  if (status === 'available') return null;
  const { label, color, icon } = TOOL_STATUS_CHIPS[status];
  return (
    <Box sx={{ mt: 'auto' }}>
      <Chip
        label={label}
        color={color}
        size="small"
        icon={icon}
        data-testid={`tool-status-chip-${status}`}
      />
    </Box>
  );
};

const AdminCard = styled(Card)(({ theme }) => ({
  height: '200px',
  transition: 'all 0.3s ease',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

/**
 * Admin Page Component
 *
 * Purpose: Provides central access to all administrative functions and tools
 * Logic: Renders admin tools with appropriate access controls and status indicators
 *
 * @returns JSX element containing the admin interface
 */
const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, hasModule } = useAuth();
  const { user } = state;

  // Check if user is in GLOBAL tenant context
  const isGlobalTenant = user?.activeTenant?.tenant_id === 'GLOBAL';

  // A tile shows only for an area the user holds (with its parent); GLOBAL-only areas also
  // need the GLOBAL tenant (ORCH-334, ORCH-21 §10.4)
  const isVisible = (tool: ToolItem) =>
    (!tool.module || hasModule(tool.module)) && (!tool.globalOnly || isGlobalTenant);

  // Breadcrumbs for the page
  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Administration', icon: <AdminIcon fontSize="inherit" /> },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const cardGridSize = { xs: 12, md: 4 };

  type ToolItem = {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    status: ToolStatus;
    path: string;
    color: string;
    /** The area the tile needs; absent for a GLOBAL-only internal tool with no area (ORCH-415). */
    module?: ModuleKey;
    globalOnly?: boolean;
  };

  // Row 1: People and data — who uses the system, what they may do, the shared lists
  const toolsRow1: ToolItem[] = [
    {
      id: 'user-management',
      title: 'User Manager',
      description: 'Invite people and manage their roles, organization and security attributes',
      icon: <GroupAddIcon sx={{ fontSize: 48, color: 'success.main' }} />,
      status: 'available',
      path: '/admin/user-management',
      module: ModuleKey.ADMIN_USER_MANAGER,
      color: 'success',
    },
    {
      id: 'role-manager',
      title: 'Role Manager',
      description: 'Build roles: which areas people open, what they can do, and on which records',
      icon: <SecurityOutlined sx={{ fontSize: 48, color: 'secondary.main' }} />,
      status: 'available',
      path: '/admin/role-manager',
      module: ModuleKey.ADMIN_ROLE_MANAGER,
      color: 'secondary',
    },
    {
      id: 'master-data-manager',
      title: 'Master Data Manager',
      description:
        'Centralized management of clients, vendors, media types, and core business data',
      icon: <StorageOutlined sx={{ fontSize: 48, color: 'info.main' }} />,
      status: 'available',
      path: '/admin/mdm',
      module: ModuleKey.ADMIN_MDM_MANAGER,
      color: 'info',
    },
  ];

  // Row 2: Automation — workflows, then the AI agents and skills that run in them
  const toolsRow2: ToolItem[] = [
    {
      id: 'workflow-manager',
      title: 'Workflow Manager',
      description: 'Define and manage workflow definitions, steps, and automation configurations',
      icon: <AccountTreeIcon sx={{ fontSize: 48, color: 'success.main' }} />,
      status: 'available',
      path: '/admin/workflow-manager',
      module: ModuleKey.ADMIN_WORKFLOW_MANAGER,
      color: 'success',
    },
    {
      id: 'ai-agent-manager',
      title: 'AI Agent Manager',
      description: 'Create and manage AI agents — skills, tool calls, and system prompts',
      icon: <SmartToyOutlined sx={{ fontSize: 48, color: 'info.main' }} />,
      status: 'available',
      path: '/admin/ai-agent-manager',
      module: ModuleKey.ADMIN_AI_AGENT_MANAGER,
      color: 'info',
    },
    {
      id: 'ai-skills-manager',
      title: 'AI Skills Manager',
      description: 'Manage skill definitions (name + markdown) available to AI agents',
      icon: <PsychologyOutlined sx={{ fontSize: 48, color: 'info.main' }} />,
      status: 'available',
      path: '/admin/ai-skills',
      module: ModuleKey.ADMIN_AI_SKILLS_MANAGER,
      color: 'info',
    },
  ];

  // Row 3: Schema / system config + utilities (Tenant Manager and Component Library are GLOBAL-only)
  const toolsRow3: ToolItem[] = [
    {
      id: 'metadata-manager',
      title: 'Metadata Manager',
      description: 'Manage metadata schemas, field definitions, and system configurations',
      icon: <SettingsApplicationsOutlined sx={{ fontSize: 48, color: 'secondary.main' }} />,
      status: 'available',
      path: '/admin/metadata-manager',
      module: ModuleKey.ADMIN_METADATA_MANAGER,
      color: 'secondary',
    },
    {
      id: 'tenant-manager',
      title: 'Tenant Manager',
      description: 'Create and manage tenant organizations, licenses, and feature access',
      icon: <BusinessIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
      status: 'available',
      path: '/admin/tenant-manager',
      color: 'warning',
      module: ModuleKey.ADMIN_TENANT_MANAGER,
      globalOnly: true,
    },
    {
      id: 'component-library',
      title: 'Component Library',
      description: 'Interactive showcase of all UI components used in the application',
      icon: <PaletteOutlined sx={{ fontSize: 48, color: 'primary.main' }} />,
      status: 'available',
      path: '/admin/component-library',
      color: 'primary',
      globalOnly: true,
    },
  ];

  const renderToolCard = (tool: ToolItem) => (
    <Grid key={tool.id} data-testid={`admin-tool-${tool.id}`} size={cardGridSize}>
      {tool.status !== 'coming-soon' ? (
        <AdminCard>
          <CardActionArea onClick={() => handleNavigate(tool.path)} sx={{ height: '100%' }}>
            <CardContent
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
                p: 3,
              }}
            >
              {tool.icon}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {tool.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {tool.description}
              </Typography>
              <ToolStatusChip status={tool.status} />
            </CardContent>
          </CardActionArea>
        </AdminCard>
      ) : (
        <ComingSoonCard>
          <CardContent
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'center',
              p: 3,
            }}
          >
            {tool.icon}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {tool.title}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {tool.description}
            </Typography>
            <ToolStatusChip status={tool.status} />
          </CardContent>
        </ComingSoonCard>
      )}
    </Grid>
  );

  return (
    <SkeletonPage pageTitle="Admin Center" breadcrumbs={breadcrumbs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        {/* Content Header */}
        <ContentHeader>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <AdminPanelSettingsOutlined sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 400 }}>
                Manage system settings, data, and platform configuration
              </Typography>
            </Box>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mt={3}>
            <Chip label={`Admin Access`} color="primary" icon={<AdminPanelSettingsOutlined />} />
            <Chip label={`User: ${user?.email?.split('@')[0] || 'Unknown'}`} variant="outlined" />
          </Box>
        </ContentHeader>
        {/* Admin Tools Section */}
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Administration Tools
        </Typography>
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 3 }}>
          {toolsRow1.filter(isVisible).map((tool) => renderToolCard(tool))}
        </Grid>
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 3 }}>
          {toolsRow2.filter(isVisible).map((tool) => renderToolCard(tool))}
        </Grid>
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
          {toolsRow3.filter(isVisible).map((tool) => renderToolCard(tool))}
        </Grid>
        {/* Footer Information - anchored to bottom of viewport via mt: auto */}
        <Box sx={{ mt: 'auto', p: 3, backgroundColor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            OrchestrateHQ AI Administration Center • Platform management and configuration tools for
            system administrators
          </Typography>
        </Box>
      </Box>
    </SkeletonPage>
  );
};

export default AdminPage;
